import type { CSSProperties } from "react";
import { AgentAvatarCardContent } from "@/components/cursors/agent-avatar-card-content";
import { cursorStatusText } from "@/components/cursors/agent-avatar-dock-utils";
import type { AgentDockItem } from "@/components/cursors/use-agent-dock-items";
import { getDurableConversation } from "@/api/conversations";
import { Button, Icons } from "@/components/ui";
import { cn } from "@/lib/cn";
import { colorWithAlpha } from "@/components/cursors/agent-cursor-glass";
import { openDurableConversationFrame } from "@/lib/open-durable-conversation-frame";
import { useAgentStore } from "@/stores/agent-store";
import { useNotificationStore } from "@/stores/notification-store";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";
import {
  navigateToCompactAgentResult,
  summonCompactAgentFollowUp,
} from "./compact-agent-status-actions";
import "@/components/cursors/agent-avatar-dock.css";
import { translate, useT } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

interface CompactAgentPanelProps {
  item: AgentDockItem;
  onBack: () => void;
  onRequestCollapse: () => void;
}

function CompactAgentBackButton({ onBack }: { onBack: () => void }) {
  const t = useT(compactMessages);
  return (
    <Button
      type="button"
      variant="ghost"
      size="iconSm"
      className="h-7 w-7 shrink-0 rounded-full text-muted hover:bg-white/10 hover:text-primary"
      aria-label={t("backToSessions")}
      data-testid="desktop-compact-agent-back"
      onClick={onBack}
    >
      <Icons.ArrowLeft className="h-3.5 w-3.5" />
    </Button>
  );
}

/**
 * Single Denker streaming card for the compact widget.
 *
 * Subagent tasks stream in the right-edge rail, so the widget never renders
 * per-agent avatar tabs — it shows the one prioritized docked Denker cursor.
 */
export function CompactAgentPanel({
  item,
  onBack,
  onRequestCollapse,
}: CompactAgentPanelProps) {
  const t = useT(compactMessages);
  const { cursor } = item;
  const hasActions = !!cursor.frozen && !cursor.error;

  const openConversation = () => {
    const conversationId = item.execution?.conversationId;
    if (!conversationId) {
      navigateToCompactAgentResult(cursor);
      return;
    }
    const isCurrentScope = captureDesktopChatAuthScopeGuard();
    void (async () => {
      try {
        const conversation = await getDurableConversation(conversationId);
        if (!isCurrentScope()) return;
        await openDurableConversationFrame({
          conversation,
          addToast: useNotificationStore.getState().addSystemToast,
          closePopover: () => {},
          selectAgent: useAgentStore.getState().selectAgent,
          shouldContinue: isCurrentScope,
        });
        onRequestCollapse();
      } catch {
        useNotificationStore.getState().addSystemToast({
          id: `compact-open-chat-${cursor.agentId}`,
          level: "error",
          title: translate(compactMessages, "openChatFailed"),
        });
      }
    })();
  };

  return (
    <div
      className={cn(
        "denker-compact-agent-panel denker-compact-agent-panel-single flex min-h-0 w-full flex-1 flex-col p-2",
      )}
      data-testid="desktop-compact-agent-panel"
      data-agent-count={1}
      style={
        {
          "--compact-agent-panel-tint": colorWithAlpha(cursor.color, 0.14),
          "--compact-agent-panel-tint-soft": colorWithAlpha(
            cursor.color,
            0.055,
          ),
        } as CSSProperties
      }
    >
      <div className="flex h-8 shrink-0 items-center justify-between gap-2 px-2">
        <span
          className="min-w-0 truncate text-appkit-mini font-semibold"
          style={{ color: cursor.color }}
          data-testid="desktop-compact-agent-name"
        >
          {cursor.name}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className="h-7 w-7 shrink-0 rounded-full text-muted hover:bg-white/10 hover:text-primary"
            aria-label={t("openConversation")}
            data-testid="desktop-compact-agent-open-conversation"
            onClick={openConversation}
          >
            <Icons.Maximize className="h-3.5 w-3.5" />
          </Button>
          <CompactAgentBackButton onBack={onBack} />
        </div>
      </div>
      <div
        className="denker-compact-agent-card min-h-0 flex-1 overflow-hidden px-2 pb-3 pt-1 text-left text-appkit-caption leading-relaxed"
        data-testid="desktop-compact-agent-card"
      >
        <AgentAvatarCardContent
          cursor={cursor}
          statusText={cursorStatusText(cursor)}
          testId="desktop-compact-agent-card"
          executionId={item.executionId}
          hasActions={hasActions}
          canAskFollowUp
          compact
          showAgentName={false}
          onNavigateToFrame={() => navigateToCompactAgentResult(cursor)}
          onAskFollowUp={(mode) => {
            summonCompactAgentFollowUp(cursor, mode);
            onRequestCollapse();
          }}
        />
      </div>
    </div>
  );
}
